/**
 * @file CtaBarRandomCard.tsx
 * @description Client-side card picker for CtaBar. Receives the full cards array from the
 * server component, renders index 0 on SSR/hydration (no mismatch), then randomizes on mount.
 * Keeping this as the only client boundary means CtaBar itself stays a server component.
 */

'use client';

import { useEffect, useState } from 'react';
import { cn } from 'tailwind-variants';
import { ICtaBar } from '@/.generated';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { ButtonWrapper } from '@/helpers/Wrappers/ButtonWrapper/ButtonWrapper';
import { getCSLPAttributes } from '@/utils/type-guards';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import { defaultVariants } from './CtaBar.styles';

type CtaBarRandomCardProps = {
  cards: NonNullable<ICtaBar['reference']>;
};

export const CtaBarRandomCard = ({ cards }: CtaBarRandomCardProps) => {
  const { wrapper, iconCol, iconWrapper, contentCtaCol, contentCol, heading, tipTitle, ctaCol } =
    defaultVariants();

  // Start at 0 so SSR and first client render agree — no hydration mismatch
  const [index, setIndex] = useState(0);

  // Randomize after mount; runs only in the browser, safely after hydration
  useEffect(() => {
    if (cards.length > 1) {
      setIndex(Math.floor(Math.random() * cards.length));
    }
  }, [cards.length]);

  const card = cards[index];
  if (!card) return null;

  const bgColor = card.card_background_color;
  const textColor = card.card_text_color?.color_list;
  const hasTextOverride = Boolean(textColor);

  // Force all rich-text descendants to inherit the override color (or default to white).
  // Drop the trailing paragraph's bottom margin so the body doesn't add extra space.
  const contentClass = cn(
    hasTextOverride ? '**:text-current' : '**:text-white',
    '[&_p:last-child]:mb-0'
  );

  return (
    <div
      className={wrapper()}
      style={{
        ...(bgColor ? { background: bgColor } : {}),
        ...(textColor ? { color: textColor } : {}),
      }}
    >
      {/* Icon column — rendered only when an image URL is present */}
      {card.bar_icon?.image?.url && (
        <div className={iconCol()}>
          {/* fill so Next.js Image doesn't require explicit dimensions when CMS has no dimension metadata */}
          <ImageWrapper
            image={card.bar_icon}
            aria-hidden="true"
            fill
            {...getCSLPAttributes(card.$?.bar_icon)}
            wrapperClassName={iconWrapper()}
          />
        </div>
      )}

      {/* Stacks content + CTA vertically below 991px; horizontal row with 30px gap above */}
      <div className={contentCtaCol()}>
        {/* Content column — tip + heading + rich-text body */}
        <div className={contentCol()}>
          <PlainTextWrapper
            content={card.tip_title}
            tag="span"
            className={tipTitle({ override: hasTextOverride })}
            cslpAttribute={card.$?.tip_title}
          />
          <PlainTextWrapper
            content={card.heading}
            tag="h3"
            className={heading({ override: hasTextOverride })}
            cslpAttribute={card.$?.heading}
          />
          <RichTextWrapper
            content={card.content}
            className={contentClass}
            style={textColor ? { color: textColor } : undefined}
            cslpAttribute={card.$?.content}
          />
        </div>

        {/* CTA column — shrink-0 prevents the button from wrapping onto a new line */}
        <div className={ctaCol()}>
          <ButtonWrapper cta={card.cta} />
        </div>
      </div>
    </div>
  );
};
